import { Injectable, signal, computed, OnDestroy, inject } from '@angular/core';
import {
  HubConnection,
  HubConnectionState,
  IHttpConnectionOptions,
  LogLevel,
  HttpTransportType,
  HubConnectionBuilder,
} from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/services/auth.service';
import { LoggerService } from '../logging/services/logger.service';

export interface RealtimeEvent {
  type: string;
  data: unknown;
  timestamp: Date;
}

@Injectable({ providedIn: 'root' })
export class SignalRService implements OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly logger = inject(LoggerService);
  private connection: HubConnection | null = null;
  private maxReconnectAttempts = 10;
  private baseReconnectDelay = 1000;
  private connectPromise: Promise<void> | null = null;
  private readonly attachedEvents = new Set<string>();
  private subscribedToNotifications = false;
  private subscribedToCommunityGroup = false;
  private readonly subscribedBrandIds = new Set<string>();
  private readonly subscribedConversationIds = new Set<string>();
  private readonly subscribedListingIds = new Set<string>();
  private readonly subscribedCollaborationIds = new Set<string>();
  private readonly subscribedCreatorIds = new Set<string>();

  private readonly _connectionState = signal<HubConnectionState>(HubConnectionState.Disconnected);
  private readonly _isConnected = signal(false);
  private readonly _lastError = signal<string | null>(null);
  private readonly _events = signal<RealtimeEvent[]>([]);

  readonly connectionState = this._connectionState.asReadonly();
  readonly isConnected = this._isConnected.asReadonly();
  readonly lastError = this._lastError.asReadonly();
  readonly events = this._events.asReadonly();

  readonly connectionStateText = computed(() => {
    const state = this._connectionState();
    if (state === HubConnectionState.Connected) return 'Connected';
    if (state === HubConnectionState.Connecting) return 'Connecting...';
    if (state === HubConnectionState.Reconnecting) return 'Reconnecting...';
    if (state === HubConnectionState.Disconnected) return 'Disconnected';
    return 'Unknown';
  });

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('app:logout', () => {
        void this.disconnect();
      });
    }
  }

  private getHubUrl(): string {
    const apiRoot = environment.apiUrl.replace(/\/api\/?$/i, '');
    return `${apiRoot}/hubs/realtime`;
  }

  async connect(token?: string): Promise<void> {
    // Don't try to connect if user is not authenticated
    const isAuthenticated = this.auth.isAuthenticated();
    if (!isAuthenticated) {
      this.logger.debug('SignalR: Skipping connection - user not authenticated');
      return;
    }

    const currentState = this._connectionState();
    if (this.connection && currentState === HubConnectionState.Connected) {
      return;
    }

    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.connectPromise = this.startConnection(token);

    try {
      await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }

  private async startConnection(token?: string): Promise<void> {
    try {
      this._connectionState.set(HubConnectionState.Connecting);
      this._lastError.set(null);
      this.logger.debug('SignalR: Attempting to connect to', this.getHubUrl());

      const accessToken = token ?? this.auth.getAccessToken();
      const options: IHttpConnectionOptions = {
        skipNegotiation: false,
        transport: HttpTransportType.WebSockets,
        withCredentials: true,
        accessTokenFactory: () => accessToken ?? '',
      };

      this.connection = new HubConnectionBuilder()
        .withUrl(this.getHubUrl(), options)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext: { previousRetryCount: number }) => {
            if (retryContext.previousRetryCount >= this.maxReconnectAttempts) {
              this.logger.warn('SignalR: Max reconnect attempts reached, giving up');
              return null;
            }
            const delay = Math.min(
              this.baseReconnectDelay * Math.pow(2, retryContext.previousRetryCount),
              30000,
            );
            this.logger.debug(`SignalR: Retry ${retryContext.previousRetryCount + 1} in ${delay}ms`);
            return delay;
          },
        })
        // SignalR writes straight to the console with its own logger, bypassing
        // LoggerService. Silence it - connection state is already reported through
        // this.logger above, which routes to the in-memory buffer and remote sink.
        .configureLogging(LogLevel.None)
        .build();

      this.attachedEvents.clear();
      this.eventHandlers.forEach((_handlers, event) => this.attachHubHandler(event));

      this.connection.onclose((error: Error | undefined) => {
        this._connectionState.set(HubConnectionState.Disconnected);
        this._isConnected.set(false);
        if (error) {
          const message = error.message || String(error);
          this._lastError.set(message);
          // Log common errors as warnings, not errors
          if (message.includes('Failed to complete negotiation') ||
              message.includes('Failed to fetch') ||
              message.includes('NetworkError')) {
            this.logger.warn('SignalR: Connection closed due to network/CORS issue', 'SignalRService', { error: error });
          } else {
            this.logger.warn('SignalR: Connection closed', 'SignalRService', { error: error });
          }
        }
      });

      this.connection.onreconnecting((error: Error | undefined) => {
        this._connectionState.set(HubConnectionState.Reconnecting);
        this._isConnected.set(false);
        const message = error?.message || String(error);
        this._lastError.set(`Reconnecting: ${message}`);
        this.logger.debug('SignalR: Reconnecting...', 'SignalRService', { error: error });
      });

      this.connection.onreconnected((connectionId: string | undefined) => {
        this._connectionState.set(HubConnectionState.Connected);
        this._isConnected.set(true);
        this._lastError.set(null);
        this.logger.info('SignalR: Reconnected', 'SignalRService', { connectionId });
        void this.restoreSubscriptions();
      });

      await this.connection.start();
      this._connectionState.set(HubConnectionState.Connected);
      this._isConnected.set(true);
      this._lastError.set(null);
      this.logger.info('SignalR: Connected successfully', 'SignalRService', {
        connectionId: this.connection.connectionId
      });
    } catch (error: unknown) {
      this._connectionState.set(HubConnectionState.Disconnected);
      this._isConnected.set(false);
      const errorMessage = error instanceof Error ? error.message : String(error);

      // Provide helpful error messages for common issues
      let userMessage = 'Failed to connect to real-time service';
      if (errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('TypeError')) {
        userMessage = 'Real-time connection failed - check network/CORS settings';
        this.logger.warn('SignalR: CORS/Network error during connection', 'SignalRService', {
          error: error,
          hubUrl: this.getHubUrl(),
          apiUrl: environment.apiUrl
        });
      } else if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        userMessage = 'Authentication required for real-time features';
        this.logger.warn('SignalR: Unauthorized', 'SignalRService', { error: error });
      } else {
        this.logger.error('SignalR: Connection failed', 'SignalRService', { error: error });
      }

      this._lastError.set(userMessage);
      // Don't throw - let the app continue without real-time features
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch {}
      this.connection = null;
      this.attachedEvents.clear();
      this.subscribedBrandIds.clear();
      this.subscribedConversationIds.clear();
      this.subscribedListingIds.clear();
      this.subscribedCollaborationIds.clear();
      this.subscribedCreatorIds.clear();
      this.subscribedToNotifications = false;
      this.subscribedToCommunityGroup = false;
      this._events.set([]);
      this._connectionState.set(HubConnectionState.Disconnected);
      this._isConnected.set(false);
    }
  }

  async reconnect(): Promise<void> {
    await this.disconnect();
    await this.connect();
  }

  /**
   * Subscribe calls are authorized server-side and reject when the caller is not
   * entitled to the group. Rejections are surfaced as a `false` return rather than a
   * thrown error so a denied subscription cannot break an unrelated caller, and the id
   * is dropped from the reconnect set so it is not retried forever.
   */
  private async invokeSubscribe(
    method: string,
    set: Set<string>,
    id: string
  ): Promise<boolean> {
    if (!this.isConnected() || !this.connection) return false;
    try {
      await this.connection.invoke(method, id);
      return true;
    } catch {
      set.delete(id);
      return false;
    }
  }

  async subscribeToBrand(brandId: string): Promise<boolean> {
    this.subscribedBrandIds.add(brandId);
    return this.invokeSubscribe('SubscribeToBrand', this.subscribedBrandIds, brandId);
  }

  async subscribeToConversation(conversationId: string): Promise<boolean> {
    this.subscribedConversationIds.add(conversationId);
    return this.invokeSubscribe('SubscribeToConversation', this.subscribedConversationIds, conversationId);
  }

  async unsubscribeFromConversation(conversationId: string): Promise<void> {
    this.subscribedConversationIds.delete(conversationId);
    if (!this.isConnected() || !this.connection) return;
    await this.connection.invoke('UnsubscribeFromConversation', conversationId);
  }

  async unsubscribeFromBrand(brandId: string): Promise<void> {
    this.subscribedBrandIds.delete(brandId);
    if (!this.isConnected() || !this.connection) return;
    await this.connection.invoke('UnsubscribeFromBrand', brandId);
  }

  async subscribeToListing(listingId: string): Promise<boolean> {
    this.subscribedListingIds.add(listingId);
    return this.invokeSubscribe('SubscribeToListing', this.subscribedListingIds, listingId);
  }

  async unsubscribeFromListing(listingId: string): Promise<void> {
    this.subscribedListingIds.delete(listingId);
    if (!this.isConnected() || !this.connection) return;
    await this.connection.invoke('UnsubscribeFromListing', listingId);
  }

  async subscribeToCollaboration(collaborationId: string): Promise<boolean> {
    this.subscribedCollaborationIds.add(collaborationId);
    return this.invokeSubscribe('SubscribeToCollaboration', this.subscribedCollaborationIds, collaborationId);
  }

  async subscribeToNotifications(): Promise<void> {
    this.subscribedToNotifications = true;
    if (!this.isConnected() || !this.connection) return;
    await this.connection.invoke('SubscribeToNotifications');
  }

  async subscribeToCreator(creatorId: string): Promise<boolean> {
    this.subscribedCreatorIds.add(creatorId);
    return this.invokeSubscribe('SubscribeToCreator', this.subscribedCreatorIds, creatorId);
  }

  async subscribeToCommunity(): Promise<void> {
    this.subscribedToCommunityGroup = true;
    if (!this.isConnected() || !this.connection) return;
    await this.connection.invoke('SubscribeToCommunity');
  }

  async unsubscribeFromCommunity(): Promise<void> {
    this.subscribedToCommunityGroup = false;
    if (!this.isConnected() || !this.connection) return;
    await this.connection.invoke('UnsubscribeFromCommunity');
  }

  async sendTypingIndicator(
    conversationId: string,
    isTyping: boolean,
    userName?: string,
  ): Promise<void> {
    if (!this.isConnected() || !this.connection) return;
    await this.connection.invoke('SendTypingIndicator', conversationId, isTyping, userName);
  }

  private eventHandlers = new Map<string, Set<(data: unknown) => void>>();

  on<T>(event: string, handler: (data: T) => void): () => void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }

    this.attachHubHandler(event);
    this.eventHandlers.get(event)!.add(handler as (data: unknown) => void);

    return () => {
      const handlers = this.eventHandlers.get(event);
      handlers?.delete(handler as (data: unknown) => void);

      if (handlers?.size === 0) {
        this.eventHandlers.delete(event);
        this.connection?.off(event);
        this.attachedEvents.delete(event);
      }
    };
  }

  off(event: string, handler?: (data: unknown) => void): void {
    if (handler) {
      const handlers = this.eventHandlers.get(event);
      handlers?.delete(handler);

      if (handlers?.size === 0) {
        this.eventHandlers.delete(event);
        this.connection?.off(event);
        this.attachedEvents.delete(event);
      }
    } else {
      this.eventHandlers.delete(event);
      this.connection?.off(event);
      this.attachedEvents.delete(event);
    }
  }

  private attachHubHandler(event: string): void {
    if (!this.connection || this.attachedEvents.has(event)) return;
    this.connection.on(event, (data: unknown) => this.handleEvent(event, data));
    this.attachedEvents.add(event);
  }

  private handleEvent(event: string, data: unknown): void {
    this._events.update((events) => [
      { type: event, data, timestamp: new Date() },
      ...events.slice(0, 99),
    ]);

    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch {}
      });
    }
  }

  clearEventLog(): void {
    this._events.set([]);
  }

  getConnectionId(): string | null {
    return this.connection?.connectionId ?? null;
  }

  private async restoreSubscriptions(): Promise<void> {
    if (!this.isConnected() || !this.connection) {
      return;
    }

    // Each subscription is restored independently. The server authorizes every
    // Subscribe* call, so a single rejected group (stale id, revoked access) must not
    // abort the whole restore and leave the client silently without realtime updates.
    // A rejected subscription is also dropped from the retry set so it is not retried
    // on every future reconnect.
    const restore = async (
      method: string,
      set?: Set<string>,
      id?: string
    ): Promise<void> => {
      try {
        if (id === undefined) {
          await this.connection!.invoke(method);
        } else {
          await this.connection!.invoke(method, id);
        }
      } catch {
        if (set && id !== undefined) {
          set.delete(id);
        }
      }
    };

    if (this.subscribedToNotifications) {
      await restore('SubscribeToNotifications');
    }
    if (this.subscribedToCommunityGroup) {
      await restore('SubscribeToCommunity');
    }

    for (const brandId of [...this.subscribedBrandIds]) {
      await restore('SubscribeToBrand', this.subscribedBrandIds, brandId);
    }
    for (const conversationId of [...this.subscribedConversationIds]) {
      await restore('SubscribeToConversation', this.subscribedConversationIds, conversationId);
    }
    for (const listingId of [...this.subscribedListingIds]) {
      await restore('SubscribeToListing', this.subscribedListingIds, listingId);
    }
    for (const collaborationId of [...this.subscribedCollaborationIds]) {
      await restore('SubscribeToCollaboration', this.subscribedCollaborationIds, collaborationId);
    }
    for (const creatorId of [...this.subscribedCreatorIds]) {
      await restore('SubscribeToCreator', this.subscribedCreatorIds, creatorId);
    }
  }

  ngOnDestroy(): void {
    this.eventHandlers.forEach((_handlers, event) => this.connection?.off(event));
    this.eventHandlers.clear();
    this.attachedEvents.clear();

    if (this.connection) {
      this.connection.stop().catch(() => {});
      this.connection = null;
    }

    this._connectionState.set(HubConnectionState.Disconnected);
    this._isConnected.set(false);
  }
}
